import React from "react";
import ReactDOM from "react-dom/client";
import { MantineProvider, ColorSchemeProvider, Global } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import App from "./App";
import "./index.css";
import { ThemeColorProvider, useThemeColor, generateShades } from "./Utilities/ThemeColorContext";

// ── "ResizeObserver loop completed with undelivered notifications" ────────────
// Chrome reports this when a ResizeObserver callback changes layout, which
// schedules another observation in the same frame. It is a browser warning, not
// a bug in this app: nothing breaks, and it never reaches a production build —
// but webpack-dev-server's overlay treats it as an uncaught error and covers the
// screen mid-work. Mantine's dropdowns (Select / Autocomplete in the transaction
// grid) sit behind floating-ui, which observes both the input and the dropdown,
// so it fires easily there.
//
// Deferring the callback by one frame breaks the same-frame loop, so the
// condition never arises. This deliberately does NOT swallow window errors —
// suppressing 'error' events would hide real ones too.
if (typeof window !== "undefined" && window.ResizeObserver) {
  const NativeResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class extends NativeResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => callback(entries, observer));
      });
    }
  };
}

function ThemedApp() {
  const [colorScheme, setColorScheme] = useLocalStorage({
    key: "mb-color-scheme",
    defaultValue: "light",
    getInitialValueInEffect: false,
  });
  const { primaryColor, customColor } = useThemeColor();

  const toggleColorScheme = (value) =>
    setColorScheme(value || (colorScheme === "dark" ? "light" : "dark"));

  return (
    <ColorSchemeProvider
      colorScheme={colorScheme}
      toggleColorScheme={toggleColorScheme}
    >
      <MantineProvider
        withGlobalStyles
        withNormalizeCSS
        theme={{
          colorScheme,
          primaryColor,
          colors: {
            cyan: [
              "#e0f2fe",
              "#bae6fd",
              "#7dd3fc",
              "#38bdf8",
              "#0ea5e9",
              "#0284c7",
              "#0369a1",
              "#075985",
              "#0c4a6e",
              "#082f49",
            ],
            custom: generateShades(customColor),
          },
        }}
      >
        <Global
          styles={(theme) => ({
            body: {
              backgroundColor:
                theme.colorScheme === "dark"
                  ? theme.colors.dark[8]
                  : "#f4f6f8",
            },
            // Read-only inputs on the transaction line-item screens are styled from
            // module-level consts (transactionUtils.RO, LineRow) that have no theme
            // access — a CSS var lets them follow dark/light without prop drilling.
            ":root": {
              // Mantine's own disabled inputs use gray[1] here, and matching it is the point:
              // a cell the user cannot type into must read the same whether it is locked
              // (a saved row's item) or simply derived (MRP, VAT, Net Amount). #f5f5f5 was too
              // close to white to say anything — the user could not tell the two apart.
              "--mb-readonly-bg":
                theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[1],
              // The greyed TEXT is what actually reads as "locked" — the background barely
              // registers at this size. Same colour Mantine gives a disabled input.
              "--mb-readonly-fg":
                theme.colorScheme === "dark" ? theme.colors.dark[2] : theme.colors.gray[6],
            },
          })}
        />
        <App />
      </MantineProvider>
    </ColorSchemeProvider>
  );
}

function Root() {
  return (
    <ThemeColorProvider>
      <ThemedApp />
    </ThemeColorProvider>
  );
}

ReactDOM.createRoot(document.getElementById("root")).render(<Root />);
